import { renderForm, renderSuccess, esc } from './ui'
import { apiGet, apiPost, apiUpload } from './api'
import { liff } from './liff'
import { BRANCHES } from './branches'
import { LINK_GROUPS } from './links'
import { getSession, isLinked, isAdmin, linkedLabel, linkedBranch, setLinked } from './session'
import { hbars, trend } from './chart'

export type Renderer = (root: HTMLElement) => void | Promise<void>

export const routes: Record<string, Renderer> = {
  '/': home,
  '/it-ticket': itTicket,
  '/new-employee': newEmployee,
  '/branch-move': branchMove,
  '/query': query,
  '/links': links,
  '/my-tickets': myTickets,
  '/link-account': linkAccount,
  '/admin': adminPanel,
}

// เปิดใช้เฉพาะเมนูนี้ก่อน — เพิ่ม path ที่นี่เมื่อจะเปิดเมนูอื่น
export const ENABLED_ROUTES = new Set([
  '/',
  '/branch-move',
  '/new-employee',
  '/it-ticket',
  '/links',
  '/my-tickets',
  '/link-account',
  '/admin',
])

// เมนูที่ต้องผูกบัญชี (ยืนยันตัวตน) ก่อนใช้
export const GATED_ROUTES = new Set(['/branch-move', '/links'])

/** เปลี่ยนหน้าแบบ SPA */
function go(path: string): void {
  history.pushState(null, '', path)
  dispatchEvent(new PopStateEvent('popstate'))
}

/** หน้าบังคับให้ผูกบัญชีก่อน (main.ts เรียกเมื่อเข้า GATED_ROUTES แล้วยังไม่ผูก) */
export function renderNeedLink(root: HTMLElement): void {
  root.innerHTML = `
    <div class="done">
      <div class="check" aria-hidden="true">🔒</div>
      <h1>ต้องยืนยันตัวตนก่อน</h1>
      <p>เมนูนี้ใช้ได้เฉพาะพนักงานที่ผูกบัญชี LINE กับ username ในระบบแล้ว</p>
      <button class="btn" id="go-link">ยืนยันตัวตน / ผูกบัญชี</button>
    </div>`
  root.querySelector('#go-link')?.addEventListener('click', () => go('/link-account'))
}

// ── หน้าแรก (เผื่อเปิด LIFF ที่ root) ─────────────────────────────
function home(root: HTMLElement): void {
  const menu: [string, string, string][] = [
    ['/branch-move', 'แจ้งย้ายสาขา', 'ย้ายสาขาให้อัตโนมัติ'],
    ['/new-employee', 'เพิ่มพนักงานใหม่', 'ขอเปิด user ในระบบ'],
    ['/it-ticket', 'แจ้งปัญหา IT / ระบบงาน', 'อุปกรณ์ • เน็ต • POS • NF • บัญชีผู้ใช้'],
    // ['/query', 'ขอข้อมูล / Query', 'ขอรายงานหรือไฟล์ CSV'],
    ['/links', 'ทางลัดโปรแกรม & URL บริษัท', 'ลิงก์ระบบและคู่มือ'],
    ['/my-tickets', 'ติดตามสถานะที่แจ้ง', 'เช็คสถานะเรื่องที่ส่งไว้'],
  ]
  if (isAdmin()) menu.push(['/admin', 'สถานะระบบ (แอดมิน)', 'เช็คพื้นที่ฐานข้อมูล'])
  const s = getSession()
  const linkRow = s.linked
    ? `<button class="linkbar linked" data-go="/link-account">✅ ยืนยันตัวตนแล้ว: <b>${esc(s.username ?? '')}</b><span>แตะเพื่อเปลี่ยนบัญชี</span></button>`
    : `<button class="linkbar" data-go="/link-account">🔒 ยืนยันตัวตน / ผูกบัญชี<span>ปลดล็อกเมนูแจ้งย้ายสาขา และทางลัดโปรแกรม</span></button>`

  root.innerHTML = `
    <header class="hd"><h1>บริการแผนกโปรแกรมเมอร์</h1></header>
    ${linkRow}
    <nav class="grid">
      ${menu
        .map(
          ([href, title, sub]) =>
            `<a class="tile${GATED_ROUTES.has(href) && !isLinked() ? ' locked' : ''}" href="${href}"><b>${esc(title)}</b><span>${esc(sub)}</span></a>`,
        )
        .join('')}
    </nav>`
  root.querySelectorAll<HTMLElement>('a.tile, [data-go]').forEach((el) =>
    el.addEventListener('click', (e) => {
      e.preventDefault()
      go(el.getAttribute('href') || el.getAttribute('data-go') || '/')
    }),
  )
}

// ── ยืนยันตัวตน / ผูกบัญชี ──────────────────────────────────────
function linkAccount(root: HTMLElement): void {
  renderForm(root, {
    title: isLinked() ? 'เปลี่ยนบัญชีที่ผูก' : 'ยืนยันตัวตน / ผูกบัญชี',
    intro: 'กรอก username และรหัสผ่านที่ใช้เข้าระบบ POS / NF เพื่อผูกกับ LINE ของคุณ',
    fields: [
      { name: 'username', label: 'Username', kind: 'text', required: true, hint: 'เช่น test.t', sanitize: (s) => s.replace(/\s+/g, '') },
      { name: 'password', label: 'รหัสผ่าน', kind: 'password', required: true },
    ],
    submitLabel: 'ยืนยันตัวตน',
    async submit(v) {
      const r = await apiPost<{ ok: boolean; username: string; name: string | null; companyCode: string | null }>(
        '/link-account',
        v,
      )
      setLinked(r.username, r.name, r.companyCode)
      return {
        title: 'ผูกบัญชีสำเร็จ ✅',
        lines: [
          `username: ${r.username}${r.name ? ` (${r.name})` : ''}`,
          'ตอนนี้ใช้เมนูแจ้งย้ายสาขา และทางลัดโปรแกรม ได้แล้ว',
        ],
      }
    },
  })
}

// ── แจ้งปัญหา IT / ระบบงาน ──────────────────────────────────────
// ต้องตรงกับ DEV_CATEGORIES ฝั่ง Worker (หมวดที่ส่งเข้ากลุ่มโปรแกรมเมอร์)
const IT_CATEGORY_OPTIONS = [
  'อุปกรณ์ · คอมพิวเตอร์/โน้ตบุ๊ก',
  'อุปกรณ์ · ปริ้นเตอร์/สแกน',
  'อุปกรณ์ · จอ/เมาส์/คีย์บอร์ด',
  'อุปกรณ์ · เครื่องอ่านบาร์โค้ด',
  'อุปกรณ์ · ลิ้นชักเก็บเงิน',
  'อุปกรณ์ · เครื่องชั่ง',
  'เครือข่าย · เน็ตช้า/หลุด',
  'เครือข่าย · WiFi',
  'เครือข่าย · เชื่อมเน็ตไม่ได้',
  'Software · Windows/Office',
  'Software · ลง/อัปเดตโปรแกรม',
  'Software · สงสัยไวรัส',
  'ระบบงาน · POS',
  'ระบบงาน · NF',
  'ระบบงาน · Probiz',
  'ระบบงาน · Inventory',
  'บัญชีผู้ใช้ · ลืมรหัสผ่าน',
  'บัญชีผู้ใช้ · แก้ไขรหัสผ่าน',
  'บัญชีผู้ใช้ · ขอสิทธิ์เพิ่ม',
  'อื่น ๆ',
]

const URGENCY_OPTIONS = ['🔴 ขายไม่ได้ / ระบบล่ม', '🟠 ทำงานบางส่วนไม่ได้', '🟢 ใช้งานได้แต่มีปัญหา']

// เปิดช่องแนบรูป/วิดีโอ — ตั้ง true เมื่อเปิด R2 + สร้าง bucket line-oa-liff-uploads แล้ว
const PHOTO_UPLOAD = false

function itTicket(root: HTMLElement): void {
  renderForm(root, {
    title: 'แจ้งปัญหา IT / ระบบงาน',
    intro: 'เลือกหมวดให้ตรง ระบบจะส่งเรื่องให้ทีมที่รับผิดชอบโดยตรง',
    fields: [
      { name: 'category', label: 'หมวดปัญหา', kind: 'select', required: true, options: IT_CATEGORY_OPTIONS },
      { name: 'urgency', label: 'ความเร่งด่วน', kind: 'radio', required: true, options: URGENCY_OPTIONS },
      { name: 'branch', label: 'สาขา / จุดที่เกิดปัญหา', kind: 'select', required: true, options: BRANCHES, value: linkedBranch() },
      { name: 'detail', label: 'รายละเอียดปัญหา', kind: 'textarea', required: true, placeholder: 'เกิดอะไร ตอนไหน เป็นบ่อยแค่ไหน' },
      { name: 'phone', label: 'เบอร์ติดต่อกลับ', kind: 'tel' },
      {
        name: 'anydesk',
        label: 'เลข AnyDesk',
        kind: 'text',
        hint: 'เปิดเครื่องและโปรแกรม AnyDesk ค้างไว้ เพื่อให้รีโมทเข้าดูได้',
        sanitize: (s) => s.replace(/[^\d ]/g, ''),
      },
      { name: 'errorMsg', label: 'ข้อความ error ที่ขึ้น', kind: 'text', placeholder: 'ถ้ามี' },
      { name: 'device', label: 'ชื่อเครื่อง / เลขครุภัณฑ์', kind: 'text', placeholder: 'ถ้ามี' },
      { name: 'tried', label: 'ลองแก้อะไรมาแล้วบ้าง', kind: 'textarea', placeholder: 'ถ้ามี' },
      ...(PHOTO_UPLOAD ? [{ name: 'photo', label: 'รูป / วิดีโอประกอบ', kind: 'file' as const }] : []),
    ],
    submitLabel: 'ส่งเรื่อง',
    async submit(v, files) {
      let photoUrl = ''
      let photoFailed = false
      if (files.photo) {
        try {
          photoUrl = (await apiUpload(files.photo)).url
        } catch {
          photoFailed = true // ยังส่งเรื่องต่อ แค่ไม่มีรูป
        }
      }
      const { ticketNo } = await apiPost<{ ticketNo: string }>('/it-ticket', { ...v, photoUrl })
      const lines = [`เลขที่รับเรื่อง: ${ticketNo}`, 'ติดตามสถานะได้ที่เมนู "ติดตามสถานะที่แจ้ง"']
      if (photoFailed) lines.push('⚠️ แนบรูปไม่สำเร็จ — ส่งเรื่องแล้วแต่ยังไม่มีรูป')
      return { title: 'ส่งเรื่องเรียบร้อย', lines }
    },
  })
}

// ── เพิ่มพนักงานใหม่ ─────────────────────────────────────────────
const DEPARTMENTS = ['ฝ่ายขาย-เคมี', 'ฝ่ายขาย-จักรกล', 'จัดซื้อ', 'บัญชี', 'สโตร์', 'การเงิน', 'HD']

// อนุญาตเฉพาะอักษรไทย / อังกฤษ (+ เว้นวรรค . -) สำหรับช่องชื่อ
const onlyThai = (s: string) => s.replace(/[^฀-๿ .\-]/g, '').replace(/ {2,}/g, ' ')
const onlyEnLower = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z .\-]/g, '')
    .replace(/ {2,}/g, ' ')

// ตรวจรหัสผ่านอ่อน — อนุญาต PIN ตัวเลขล้วน/4 หลักได้ แต่กันที่เดาง่ายจริง ๆ
// คืนข้อความ error ถ้าไม่ผ่าน, คืน '' ถ้าผ่าน
const WEAK_PASSWORDS = new Set([
  'password', 'passw0rd', 'admin', 'qwerty', 'qwertyuiop', 'asdfgh', 'abc123',
  'iloveyou', 'test', 'test123', 'pwc', 'pwc123', 'pwc1234', '1234567890',
])
function isSequential(pw: string): boolean {
  if (pw.length < 3) return false
  let inc = true
  let dec = true
  for (let i = 1; i < pw.length; i++) {
    const d = pw.charCodeAt(i) - pw.charCodeAt(i - 1)
    if (d !== 1) inc = false
    if (d !== -1) dec = false
  }
  return inc || dec
}
function checkPassword(pw: string): string {
  if (pw.length < 4) return 'รหัสผ่านต้องยาวอย่างน้อย 4 ตัว'
  if (/^(.)\1+$/.test(pw)) return 'รหัสผ่านต้องไม่ใช่ตัวเดียวซ้ำ ๆ เช่น 1111'
  if (isSequential(pw)) return 'รหัสผ่านเรียงลำดับแบบนี้เดาง่ายเกินไป เช่น 1234'
  if (WEAK_PASSWORDS.has(pw.toLowerCase())) return 'รหัสผ่านนี้เดาง่ายเกินไป กรุณาตั้งใหม่'
  return ''
}

function newEmployee(root: HTMLElement): void {
  renderForm(root, {
    title: 'เพิ่มพนักงานใหม่',
    intro: 'ขอเปิด user ในระบบ NF / Probiz / POS — ทีมโปรแกรมเมอร์จะดำเนินการและแจ้งผลกลับทาง LINE นี้',
    fields: [
      {
        name: 'fullNameTh',
        label: 'ชื่อ-นามสกุล (ภาษาไทย)',
        kind: 'text',
        required: true,
        hint: 'พิมพ์เป็นภาษาไทยเท่านั้น',
        sanitize: onlyThai,
      },
      {
        name: 'fullNameEn',
        label: 'ชื่อ-นามสกุล (ภาษาอังกฤษ)',
        kind: 'text',
        required: true,
        hint: 'ภาษาอังกฤษ ตัวพิมพ์เล็กทั้งหมด',
        sanitize: onlyEnLower,
      },
      { name: 'nicknameTh', label: 'ชื่อเล่น (ภาษาไทย)', kind: 'text', sanitize: onlyThai },
      {
        name: 'password',
        label: 'รหัสผ่าน',
        kind: 'text',
        required: true,
        hint: 'อย่างน้อย 4 ตัว ห้ามเดาง่าย เช่น 1234, 1111',
      },
      { name: 'position', label: 'ตำแหน่ง', kind: 'text' },
      { name: 'department', label: 'สังกัดแผนก', kind: 'radio', options: DEPARTMENTS, allowOther: true },
      { name: 'branch', label: 'ประจำที่สาขา', kind: 'select', required: true, options: BRANCHES },
      {
        name: 'trainedAtHQ',
        label: 'ฝึกงานที่สาขา 00 สำนักงานใหญ่ ก่อนหรือไม่',
        kind: 'radio',
        required: true,
        options: ['ใช่', 'ไม่ใช่'],
        hint: 'หากฝึกงานเสร็จแล้ว จะต้องย้ายสาขาไปประจำสาขาตนเองด้วย (เมนู "แจ้งย้ายสาขา")',
      },
      {
        name: 'resignedStaff',
        label: 'รายชื่อพนักงานที่ลาออกล่าสุด',
        kind: 'text',
        required: true,
        hint: 'พนักงานสาขา/แผนกของท่านที่ลาออกแล้วแต่ยังมีชื่อในระบบ เพื่อให้ลบ username (หากไม่มีให้ระบุ -)',
      },
    ],
    submitLabel: 'ส่งให้ทีมโปรแกรมเมอร์',
    async submit(v) {
      const pwErr = checkPassword(v.password ?? '')
      if (pwErr) throw new Error(pwErr)
      const r = await apiPost<{ ticketNo: string; suggestedUsername?: string }>('/new-employee', v)
      const lines = [`เลขที่รับเรื่อง: ${r.ticketNo}`]
      if (r.suggestedUsername) lines.push(`username เบื้องต้น: ${r.suggestedUsername} (รอทีมโปรแกรมเมอร์ยืนยัน)`)
      lines.push('ทีมโปรแกรมเมอร์จะดำเนินการและแจ้งผลกลับทาง LINE นี้')
      return { title: 'ส่งเรื่องเรียบร้อย', lines }
    },
  })
}

// ── แจ้งย้ายสาขา ─────────────────────────────────────────────────
const MOVE_REASONS = [
  'จบการฝึกงานที่ สนญ ย้ายไปประจำสาขาตนเอง',
  'ไปช่วยงานสาขาชั่วคราว (เนื่องจาก พนง.ประจำสาขาหยุด)',
  'ไปช่วยงานสาขาชั่วคราว (เนื่องจาก พนง. ไม่เพียงพอ)',
  'ย้ายกลับสาขาตนเอง',
  'ทำเอกสาร',
]

const MOVE_ACK =
  'หลังจากส่งแบบฟอร์มนี้ ให้ลอง login เข้าระบบได้ทันที ' +
  '(แต่สำหรับ POS ออฟไลน์ ท่านจะต้องกดซิงค์ข้อมูลพนักงานใหม่ที่หน้า login ของ POS ด้วยตนเอง) ' +
  'หากสาขายังไม่ถูกย้าย จึงทำการแจ้งโปรแกรมเมอร์ — ท่านรับทราบข้อความข้างต้นนี้ใช่หรือไม่'

function branchMove(root: HTMLElement): void {
  renderForm(root, {
    title: 'แจ้งย้ายสาขา',
    intro: 'ระบบจะย้ายสาขาให้อัตโนมัติหลังตรวจสอบ',
    fields: [
      {
        name: 'name',
        label: 'พนักงาน',
        kind: 'text',
        required: true,
        hint: 'ดึงจากบัญชีที่ยืนยันตัวตนไว้',
        value: linkedLabel(),
        readonly: true,
      },
      { name: 'fromBranch', label: 'ย้ายจากสาขา', kind: 'select', required: true, options: BRANCHES },
      { name: 'toBranch', label: 'ไปที่สาขา', kind: 'select', required: true, options: BRANCHES },
      { name: 'reason', label: 'ย้ายสาขาเนื่องจาก', kind: 'radio', required: true, options: MOVE_REASONS, allowOther: true },
      { name: 'acknowledged', label: 'การรับทราบ', kind: 'checkbox', required: true, text: MOVE_ACK },
    ],
    submitLabel: 'ยืนยันการย้ายสาขา',
    async submit(v) {
      if (v.fromBranch === v.toBranch) throw new Error('สาขาต้นทางและปลายทางต้องไม่ใช่สาขาเดียวกัน')
      const r = await apiPost<{ ticketNo: string; status?: string; detail?: string }>('/branch-move', v)
      const lines = [`เลขที่รับเรื่อง: ${r.ticketNo}`, `สถานะ: ${r.status ?? 'กำลังดำเนินการ'}`]
      if (r.detail) lines.push(r.detail)
      return { title: 'รับเรื่องย้ายสาขาแล้ว', lines }
    },
  })
}

// ── ขอข้อมูล / Query ────────────────────────────────────────────
function query(root: HTMLElement): void {
  renderForm(root, {
    title: 'ขอข้อมูล / Query',
    intro: 'ระบุรายงานที่ต้องการ ทีมโปรแกรมเมอร์จะจัดทำและส่งไฟล์กลับทาง LINE',
    fields: [
      { name: 'reportName', label: 'ชื่อรายงาน / ข้อมูลที่ต้องการ', kind: 'text', required: true },
      { name: 'purpose', label: 'นำไปใช้ทำอะไร', kind: 'textarea', required: true },
      { name: 'dateFrom', label: 'ช่วงข้อมูล: ตั้งแต่', kind: 'date' },
      { name: 'dateTo', label: 'ช่วงข้อมูล: ถึง', kind: 'date' },
      { name: 'filters', label: 'เงื่อนไข / filter เพิ่มเติม', kind: 'textarea', placeholder: 'เช่น เฉพาะสาขา A, เฉพาะสินค้าหมวด X' },
      { name: 'format', label: 'รูปแบบไฟล์', kind: 'select', options: ['CSV', 'Excel (.xlsx)'] },
    ],
    submitLabel: 'ส่งคำขอ',
    async submit(v) {
      const { ticketNo } = await apiPost<{ ticketNo: string }>('/query', v)
      return { title: 'ส่งคำขอเรียบร้อย', lines: [`เลขที่รับเรื่อง: ${ticketNo}`, 'จะได้รับไฟล์ทาง LINE เมื่อจัดทำเสร็จ'] }
    },
  })
}

// ── ทางลัดโปรแกรม & URL ─────────────────────────────────────────
function links(root: HTMLElement): void {
  const groups = LINK_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => /^https?:\/\//.test(i.url)), // ซ่อนรายการที่ยังไม่ใส่ URL
  })).filter((g) => g.items.length > 0)

  root.innerHTML = `
    <header class="hd"><h1>ทางลัดโปรแกรม &amp; URL บริษัท</h1></header>
    ${groups
      .map(
        (g) => `<section class="card">
          <h2>${esc(g.title)}</h2>
          <div class="linklist">
            ${g.items
              .map(
                (i) =>
                  `<a href="${esc(i.url)}" data-ext><b>${esc(i.label)}</b>${i.note ? `<span>${esc(i.note)}</span>` : ''}</a>`,
              )
              .join('')}
          </div>
        </section>`,
      )
      .join('')}`

  root.querySelectorAll<HTMLAnchorElement>('a[data-ext]').forEach((a) =>
    a.addEventListener('click', (e) => {
      e.preventDefault()
      const url = a.href
      if (liff.isInClient()) liff.openWindow({ url, external: true })
      else window.open(url, '_blank', 'noopener')
    }),
  )
}

// ── ติดตามสถานะที่แจ้ง ──────────────────────────────────────────
interface TicketRow {
  ticket_no: string
  type: string
  status: string
  summary: string
  created_at: string
}

async function myTickets(root: HTMLElement): Promise<void> {
  root.innerHTML = `<div class="loading">กำลังโหลด…</div>`
  const { tickets } = await apiGet<{ tickets: TicketRow[] }>('/tickets')
  if (!tickets.length) {
    renderSuccess(root, { title: 'ยังไม่มีเรื่องที่แจ้ง', lines: ['เมื่อคุณส่งเรื่องผ่านเมนู จะแสดงที่นี่'] })
    return
  }
  const typeLabel: Record<string, string> = {
    it: 'แจ้งปัญหา IT',
    move: 'ย้ายสาขา',
    employee: 'พนักงานใหม่',
    query: 'ขอข้อมูล / Query',
  }
  const statusMeta: Record<string, [string, string]> = {
    new: ['รอรับเรื่อง', '#8A9E82'],
    in_progress: ['กำลังดำเนินการ', '#C77D2E'],
    done: ['เสร็จสิ้น', '#2F6B34'],
    rejected: ['ไม่อนุมัติ', '#B23B3B'],
  }
  root.innerHTML = `
    <header class="hd"><h1>ติดตามสถานะที่แจ้ง</h1></header>
    <div class="tickets">
      ${tickets
        .map((t) => {
          const [label, color] = statusMeta[t.status] ?? [t.status, '#8A9E82']
          return `<div class="tk">
            <div class="tk-top">
              <span class="tk-no">${esc(t.ticket_no)}</span>
              <span class="tk-badge" style="background:${color}">${esc(label)}</span>
            </div>
            <div class="tk-type">${esc(typeLabel[t.type] ?? t.type)}${t.summary ? ` · ${esc(t.summary)}` : ''}</div>
            <div class="tk-date">${new Date(t.created_at).toLocaleString('th-TH')}</div>
          </div>`
        })
        .join('')}
    </div>`
}

// ── สถานะระบบ + รายงาน (แอดมิน) ────────────────────────────────
interface AdminStats {
  limitMB: number
  usedMB: number
  pct: number
  tickets: { total: number; oldest: string | null; newest: string | null; byStatus: Record<string, number> }
  linkedAccounts: number
  linkedList: { username: string; name: string | null; company_code: string | null; linked_at: string }[]
}
type Row = Record<string, any>
interface AdminReport {
  days: number
  perDay: Row[]
  byType: Row[]
  byItCategory: Row[]
  byBranch: Row[]
  resolutionByUrgency: Row[]
  responseTime: { avg_h: number | null; n: number }
  byHandler: Row[]
  rejected: Row[]
  moveOutcome: Row[]
  moveReason: Row[]
  linkedPerMonth: Row[]
  topRequesters: Row[]
  stuck: Row[]
}

const TYPE_LABEL: Record<string, string> = {
  it: 'แจ้งปัญหา IT',
  move: 'แจ้งย้ายสาขา',
  employee: 'เพิ่มพนักงานใหม่',
  query: 'ขอข้อมูล / Query',
}
const MOVE_OUTCOME_LABEL: Record<string, string> = {
  done: 'ย้ายอัตโนมัติสำเร็จ',
  no_change: 'อยู่สาขานั้นแล้ว',
  pending_manual: 'ทีมโปรแกรมเมอร์ทำมือ',
  processing: 'กำลังดำเนินการ',
  error: 'ผิดพลาด',
}
const dDate = (x: string | null) => (x ? new Date(x).toLocaleDateString('th-TH') : '—')
const ageDays = (x: string) => Math.floor((Date.now() - new Date(x).getTime()) / 86400_000)
const hrs = (h: number | null | undefined) =>
  h == null ? '—' : h < 1 ? `${Math.round(h * 60)} นาที` : h < 48 ? `${h.toFixed(1)} ชม.` : `${(h / 24).toFixed(1)} วัน`

async function adminPanel(root: HTMLElement): Promise<void> {
  root.innerHTML = `<div class="loading">กำลังโหลด…</div>`
  let s: AdminStats
  try {
    s = await apiGet<AdminStats>('/admin/stats')
  } catch (err) {
    renderSuccess(root, {
      title: 'เข้าไม่ได้',
      lines: [err instanceof Error ? err.message : String(err), 'ต้องเป็นแอดมินและผูกบัญชีก่อน'],
    })
    return
  }

  let days = 30
  const render = async (): Promise<void> => {
    root.innerHTML = `<div class="loading">กำลังโหลด…</div>`
    const rep = await apiGet<AdminReport>(`/admin/report?days=${days}`)
    root.innerHTML = adminHtml(s, rep, days)
    root.querySelectorAll<HTMLElement>('[data-days]').forEach((b) =>
      b.addEventListener('click', () => {
        days = Number(b.dataset.days)
        render()
      }),
    )
    const resendBtn = root.querySelector<HTMLButtonElement>('#resend')
    resendBtn?.addEventListener('click', async () => {
      resendBtn.disabled = true
      resendBtn.textContent = 'กำลังส่ง…'
      try {
        const rs = await apiPost<{ sent: number; failed: number; checked: number }>('/admin/resend?days=3', {})
        resendBtn.textContent = `ส่งแล้ว ${rs.sent}/${rs.checked} เรื่อง${rs.failed ? ` · fail ${rs.failed}` : ''}`
      } catch (err) {
        resendBtn.textContent = err instanceof Error ? err.message : 'ผิดพลาด'
      }
    })
  }
  await render()
}

function adminHtml(s: AdminStats, r: AdminReport, days: number): string {
  const spaceColor = s.pct >= 80 ? '#B23B3B' : s.pct >= 60 ? '#C77D2E' : '#2F6B34'
  const st = s.tickets.byStatus
  const filter = [7, 30, 90]
    .map((n) => `<button class="chip${n === days ? ' on' : ''}" data-days="${n}">${n} วัน</button>`)
    .join('')

  // สาขาที่ยังไม่มีใครผูกบัญชี
  const linkedCodes = new Set(s.linkedList.map((u) => (u.company_code ?? '').replace(/\D/g, '').slice(-2)).filter(Boolean))
  const noLink = BRANCHES.filter((b) => !linkedCodes.has(b.slice(0, 2)))

  const moveTotal = r.moveOutcome.reduce((a, b) => a + b.n, 0)

  return `
    <header class="hd"><h1>รายงานระบบ</h1></header>
    <div class="chips">${filter}</div>

    <section class="card">
      <h2>พื้นที่ฐานข้อมูล</h2>
      <div class="meter"><div class="meter-fill" style="width:${Math.min(s.pct, 100)}%;background:${spaceColor}"></div></div>
      <p style="margin:8px 0 0;font-weight:700;color:${spaceColor}">${s.usedMB} MB / ${s.limitMB} MB (${s.pct}%)</p>
      ${s.pct >= 80 ? `<p style="color:#B23B3B;font-size:14px;margin-top:8px">⚠️ ใกล้เต็ม — ควร archive เรื่องปิดแล้วที่เก่ากว่า 6 เดือน</p>` : ''}
    </section>

    <section class="card">
      <h2>เรื่องแจ้งใน ${days} วัน</h2>
      ${trend(r.perDay.map((x) => [x.d as string, x.n as number]))}
      <div class="statlist" style="margin-top:6px">
        <div><span>ทั้งหมด (สะสม)</span><b>${s.tickets.total.toLocaleString('th-TH')}</b></div>
        <div><span>รอรับเรื่อง</span><b>${st.new ?? 0}</b></div>
        <div><span>กำลังดำเนินการ</span><b>${st.in_progress ?? 0}</b></div>
        <div><span>เสร็จสิ้น</span><b>${st.done ?? 0}</b></div>
        <div><span>ตีกลับ</span><b>${st.rejected ?? 0}</b></div>
      </div>
      <button class="btn" id="resend" style="margin-top:14px">ส่งการ์ดเข้ากลุ่มซ้ำ (เรื่องที่ยังรอรับ 3 วันล่าสุด)</button>
      <p class="muted-sm">ใช้กรณี push เดิม fail — จะส่งเฉพาะเรื่องสถานะ "รอรับเรื่อง"</p>
    </section>

    <section class="card">
      <h2>แยกตามประเภท (${days} วัน)</h2>
      ${hbars(r.byType.map((x) => [TYPE_LABEL[x.type] ?? x.type, x.n]), { unit: 'เรื่อง' })}
    </section>

    <section class="card">
      <h2>หมวดปัญหา IT ยอดฮิต</h2>
      ${hbars(r.byItCategory.map((x) => [x.cat as string, x.n]), { unit: 'เรื่อง' })}
    </section>

    <section class="card">
      <h2>สาขาที่แจ้งเยอะสุด</h2>
      ${hbars(r.byBranch.map((x) => [x.br as string, x.n]), { unit: 'เรื่อง' })}
    </section>

    <section class="card">
      <h2>ความเร็วในการตอบ</h2>
      <div class="statlist">
        <div><span>เฉลี่ย แจ้ง → รับเรื่อง</span><b>${hrs(r.responseTime.avg_h)}</b></div>
      </div>
      <h3 class="sub-h">เฉลี่ย แจ้ง → ปิดงาน (เฉพาะ IT)</h3>
      ${hbars(
        r.resolutionByUrgency.map((x) => [x.u as string, Math.round((x.avg_h as number) * 10) / 10, `${x.n} เรื่อง`]),
        { unit: 'ชม.' },
      )}
    </section>

    <section class="card">
      <h2>เรื่องค้างเกิน 3 วัน (${r.stuck.length})</h2>
      ${
        r.stuck.length
          ? `<div class="statlist">${r.stuck
              .map(
                (x) => `<div>
                  <span><b style="color:var(--green-dark)">${esc(x.ticket_no)}</b> · ${esc(
                    TYPE_LABEL[x.type] ?? x.type,
                  )}${x.cat ? ` · ${esc(x.cat)}` : ''} · ${esc(x.display_name ?? '')}</span>
                  <span style="color:#B23B3B;font-size:12px">${ageDays(x.created_at)} วัน</span>
                </div>`,
              )
              .join('')}</div>`
          : `<p class="muted-sm">ไม่มีเรื่องค้าง 👍</p>`
      }
    </section>

    <section class="card">
      <h2>งานต่อผู้รับเรื่อง (${days} วัน)</h2>
      ${hbars(r.byHandler.map((x) => [x.handler as string, x.n, `ปิดแล้ว ${x.done}`]), { unit: 'เรื่อง' })}
    </section>

    <section class="card">
      <h2>ย้ายสาขา (${days} วัน) — ${moveTotal} เรื่อง</h2>
      ${hbars(r.moveOutcome.map((x) => [MOVE_OUTCOME_LABEL[x.s] ?? x.s ?? 'ไม่ทราบ', x.n]), { unit: 'เรื่อง' })}
      <h3 class="sub-h">เหตุผลการย้าย</h3>
      ${hbars(r.moveReason.map((x) => [x.r as string, x.n]), { unit: 'ครั้ง' })}
    </section>

    <section class="card">
      <h2>ผู้แจ้งบ่อยสุด (${days} วัน)</h2>
      ${hbars(r.topRequesters.map((x) => [x.who as string, x.n], ), { unit: 'เรื่อง' })}
    </section>

    ${
      r.rejected.length
        ? `<section class="card">
      <h2>เรื่องตีกลับ (${days} วัน)</h2>
      <div class="statlist">${r.rejected
        .map(
          (x) => `<div><span><b>${esc(x.ticket_no)}</b> · ${esc(TYPE_LABEL[x.type] ?? x.type)}${
            x.cat ? ` · ${esc(x.cat)}` : ''
          }</span><span style="font-size:12px">${dDate(x.updated_at)}</span></div>`,
        )
        .join('')}</div>
    </section>`
        : ''
    }

    <section class="card">
      <h2>การผูกบัญชี</h2>
      <div class="statlist"><div><span>ผูกแล้วทั้งหมด</span><b>${s.linkedAccounts}</b></div></div>
      ${r.linkedPerMonth.length > 1 ? trend(r.linkedPerMonth.map((x) => [x.m as string, x.n as number]), { noun: 'บัญชี' }) : ''}
      ${
        noLink.length
          ? `<h3 class="sub-h">สาขาที่ยังไม่มีใครผูก (${noLink.length})</h3>
             <p class="muted-sm">${noLink.map((b) => esc(b)).join(' · ')}</p>`
          : `<p class="muted-sm" style="margin-top:8px">ทุกสาขามีคนผูกบัญชีแล้ว 🎉</p>`
      }
    </section>

    <section class="card">
      <h2>ผู้ใช้ที่ยืนยันตัวตนแล้ว</h2>
      ${
        s.linkedList.length
          ? `<div class="statlist">${s.linkedList
              .map(
                (u) => `<div><span><b style="color:var(--green-dark);font-size:15px">${esc(u.username)}</b>${
                  u.name ? ` · ${esc(u.name)}` : ''
                }${u.company_code ? ` · ${esc(u.company_code)}` : ''}</span><span style="font-size:12px">${dDate(
                  u.linked_at,
                )}</span></div>`,
              )
              .join('')}</div>`
          : `<p class="muted-sm">ยังไม่มีใครผูกบัญชี</p>`
      }
    </section>`
}
